import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { FaBars, FaTimes, FaChevronDown } from "react-icons/fa";
import axios from "axios";
import API_BASE_URL from "../config";
import "./Navbar.css";

const slugify = (text = "") => {
  return text
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)/g, "");
};

const mapServicePath = (title = "") => {
  const slug = slugify(title);
  return slug ? `/services/${slug}` : "/services";
};

const DEFAULT_SUBLINKS = [
  { name: "All Businesses", path: "/services" },
];

const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [mobile, setMobile] = useState(false);
  const [dropdown, setDropdown] = useState(false);
  const [dynamicServices, setDynamicServices] = useState(DEFAULT_SUBLINKS);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 30);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    setMobile(false);
    setDropdown(false);
  }, [location]);

  useEffect(() => {
    document.body.style.overflow = mobile ? "hidden" : "auto";
    return () => {
      document.body.style.overflow = "auto";
    };
  }, [mobile]);

  useEffect(() => {
    const fetchServices = async () => {
      try {
        const res = await axios.get(`${API_BASE_URL}/public/landing/`);
        const servicesList = res.data?.services || [];

        if (!servicesList.length) {
          setDynamicServices(DEFAULT_SUBLINKS);
          return;
        }

        const seen = new Set();
        const dropdownLinks = [];

        servicesList.forEach((s) => {
          const title = s.title || "Business";
          const path = mapServicePath(title);

          if (!seen.has(path)) {
            seen.add(path);
            dropdownLinks.push({
              name: title,
              path: path,
            });
          }
        });

        setDynamicServices([
          { name: "All Businesses", path: "/services" },
          ...dropdownLinks,
        ]);
      } catch (err) {
        console.error("Failed to fetch dynamic services for Navbar:", err);
        setDynamicServices(DEFAULT_SUBLINKS);
      }
    };
    fetchServices();
  }, []);

  const navLinks = [
    { name: "Home", path: "/" },
    { name: "About", path: "/about" },
    {
      name: "Our Businesses",
      path: "/services",
      isDropdown: true,
      subLinks: dynamicServices,
    },
    { name: "Gallery", path: "/gallery" },
  ];

  return (
    <header className="header-wrapper">
      <nav className={`navbar ${scrolled ? "scrolled" : ""}`}>
        <div className="container nav-content">
          {/* LOGO */}
          <Link to="/" className="logo" onClick={() => setMobile(false)}>
            <img src="/Sarwa Logo.png" alt="Sarwa Group Logo" />
          </Link>

          {/* DESKTOP & MOBILE MENU */}
          <ul className={`nav-links ${mobile ? "open" : ""}`}>
            <li className="mobile-head">
              <img src="/Sarwa Logo.png" alt="Sarwa Group" />
              <button
                type="button"
                className="close-btn"
                onClick={() => setMobile(false)}
                aria-label="Close Menu"
              >
                <FaTimes />
              </button>
            </li>

            {navLinks.map((item) => {
              if (item.isDropdown) {
                const isServicesActive = location.pathname.startsWith("/services");
                return (
                  <li
                    key={item.name}
                    className={`nav-item dropdown-parent ${
                      dropdown ? "active-dropdown" : ""
                    }`}
                    onMouseEnter={() => window.innerWidth > 1024 && setDropdown(true)}
                    onMouseLeave={() => window.innerWidth > 1024 && setDropdown(false)}
                  >
                    <button
                      type="button"
                      className={`nav-link dropdown-toggle ${
                        isServicesActive ? "active" : ""
                      }`}
                      onClick={() => setDropdown((prev) => !prev)}
                    >
                      {item.name}
                      <FaChevronDown
                        className={`chevron-icon ${dropdown ? "rotate" : ""}`}
                      />
                    </button>

                    <ul className={`dropdown-menu ${dropdown ? "show" : ""}`}>
                      {item.subLinks.map((sub) => (
                        <li key={`${sub.name}-${sub.path}`}>
                          <Link
                            to={sub.path}
                            className={`dropdown-link ${
                              location.pathname === sub.path ? "active" : ""
                            }`}
                            onClick={() => {
                              setDropdown(false);
                              setMobile(false);
                            }}
                          >
                            {sub.name}
                          </Link>
                        </li>
                      ))}
                    </ul>
                  </li>
                );
              }

              return (
                <li key={item.name} className="nav-item">
                  <Link
                    to={item.path}
                    className={`nav-link ${
                      location.pathname === item.path ? "active" : ""
                    }`}
                    onClick={() => setMobile(false)}
                  >
                    {item.name}
                  </Link>
                </li>
              );
            })}
          </ul>

          {/* MOBILE HAMBURGER MENU BUTTON */}
          <div className="nav-right">
            <button
              type="button"
              className="menu-btn"
              onClick={() => setMobile(true)}
              aria-label="Open Menu"
            >
              <FaBars />
            </button>
          </div>
        </div>

        {/* Mobile Backdrop */}
        {mobile && <div className="overlay" onClick={() => setMobile(false)} />}
      </nav>
    </header>
  );
};

export default Navbar;